import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, Ticket, Sparkles, CheckCircle2, ScanSearch } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { none: "Bez predplatného", trialing: "Skúšobná doba (14 dní)", active: "Aktívne", past_due: "Platba zlyhala", canceled: "Zrušené" };
const eurc = (cents) => `${(Number(cents || 0) / 100).toFixed(2).replace(".", ",")} €`;

export default function BillingPanel() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [annual, setAnnual] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/billing/me"); setInfo(data); } catch (e) { console.error("billing/me load failed", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, annual, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Nepodarilo sa spustiť platbu"); setBusy(""); }
  };
  const portal = async () => {
    setBusy("portal");
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error("Správu predplatného sa nepodarilo otvoriť"); setBusy(""); }
  };

  if (!info) return <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const costs = info.costs || { spz: 1, ai: 1 };

  if (info.unlimited) {
    return (
      <div className="space-y-3" data-testid="billing-panel">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Neobmedzený prístup</h3>
        <p className="text-sm text-muted-foreground">Váš účet má plný prístup bez limitov a bez platby. Overenia ŠPZ aj AI normohodiny sú neobmedzené (bez míňania kreditov).</p>
      </div>
    );
  }

  const plans = info.plans || {};
  return (
    <div className="space-y-5" data-testid="billing-panel">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background/50 p-4">
        <Ticket className="h-6 w-6 text-primary" />
        <div className="mr-auto">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Kredity (dnes)</div>
          <div className="font-heading text-2xl font-bold" data-testid="billing-credits">{info.spz_credits ?? "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Stav</div>
          <div className="font-semibold" data-testid="billing-status">{STATUS_LABEL[info.sub_status] || info.sub_status}</div>
          {info.trial_end && <div className="text-xs text-muted-foreground">skúšobná doba do {String(info.trial_end).slice(0, 10)}</div>}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 rounded-lg border border-border bg-background/40 p-4 text-sm">
        <div className="flex items-center gap-2"><ScanSearch className="h-5 w-5 text-primary" /> 1 overenie ŠPZ = <strong className="text-foreground">{costs.spz}</strong> {costs.spz === 1 ? "kredit" : "kreditov"}</div>
        <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> 1 AI výpočet normohodín = <strong className="text-foreground">{costs.ai}</strong> {costs.ai === 1 ? "kredit" : "kreditov"}</div>
      </div>

      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-lg border border-border bg-background/60 p-1" data-testid="billing-period-toggle">
          <button type="button" onClick={() => setAnnual(false)} data-testid="period-monthly" className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${!annual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Mesačne</button>
          <button type="button" onClick={() => setAnnual(true)} data-testid="period-annual" className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${annual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Ročne · 2 mesiace zdarma</button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {["maly", "velky"].map((k) => plans[k] && (
          <div key={k} className="flex flex-col rounded-xl border border-border bg-card p-5" data-testid={`plan-card-${k}`}>
            <div className="font-heading text-xl font-bold uppercase">{plans[k].name}</div>
            <div className="mt-1 font-heading text-3xl font-extrabold text-primary">{annual ? eurc(plans[k].price_cents * 10) : eurc(plans[k].price_cents)}<span className="text-sm font-normal text-muted-foreground">{annual ? "/rok" : "/mes."}</span></div>
            <div className="mt-0.5 text-xs text-muted-foreground" data-testid={`plan-vat-${k}`}>Cena je uvedená bez DPH</div>
            {annual && <div className="mt-0.5 text-xs font-semibold text-emerald-400" data-testid={`plan-save-${k}`}>Ušetríte {eurc(plans[k].price_cents * 2)} ročne (2 mesiace zdarma)</div>}
            <ul className="mt-3 flex-1 space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Štart {plans[k].spz_start} kreditov</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> +{plans[k].spz_daily}/deň (max {plans[k].spz_cap})</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Neobmedzené zákazky a faktúry</li>
              <li className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI normohodiny za kredity</li>
            </ul>
            <Button className="mt-4 min-h-[48px] gap-2" onClick={() => subscribe(k)} disabled={!!busy} data-testid={`subscribe-${k}`}>
              {busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Vyskúšať 14 dní zdarma
            </Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Pri objednaní zadáte platobnú kartu, no <strong className="text-foreground">14 dní platíte 0 €</strong>. Po skončení skúšobnej doby sa automaticky strhne {annual ? "prvá ročná platba (2 mesiace zdarma)" : "prvá mesačná platba"}. Zrušiť môžete kedykoľvek.</p>
      {info.has_customer && <Button variant="outline" onClick={portal} disabled={!!busy} className="min-h-[44px] gap-2" data-testid="billing-portal"><CreditCard className="h-4 w-4" /> Spravovať predplatné / kartu / faktúry</Button>}
    </div>
  );
}
